/*
 * Bidder session storage. The signed token from otp-verify is kept in
 * localStorage (not sessionStorage) so it is shared across tabs — tapping an
 * outbid text's deep link opens a new tab, and we don't want that to force a
 * re-login. The token is sent only in the X-Bidder-Token header, never in a
 * URL, and the server still enforces its ~12h expiry and revocation.
 */

const TOKEN_KEY = "eball_bidder_token";
const BIDDER_KEY = "eball_bidder_info";

export interface Bidder {
	bidNumber: number | null;
	firstName: string | null;
	lastName: string | null;
	role: string | null;
}

const session = {
	set(token: string, bidder: Bidder): void {
		localStorage.setItem(TOKEN_KEY, token);
		localStorage.setItem(BIDDER_KEY, JSON.stringify(bidder));
	},

	token(): string | null {
		return localStorage.getItem(TOKEN_KEY);
	},

	bidder(): Bidder | null {
		const raw = localStorage.getItem(BIDDER_KEY);
		if (!raw) return null;
		try {
			return JSON.parse(raw) as Bidder;
		} catch (e) {
			return null;
		}
	},

	bidNumber(): number | null {
		const bidder = session.bidder();
		return bidder ? bidder.bidNumber : null;
	},

	// The signed token's payload carries its expiry. Decode it (read-only — the
	// server still verifies the signature) so an expired token is treated as
	// logged-out up front, rather than silently failing on the next action.
	expiresAt(): number | null {
		const token = session.token();
		if (!token || token.indexOf(".") === -1) return null;

		try {
			const payload = token.split(".")[0].replace(/-/g, "+").replace(/_/g, "/");
			const decoded = JSON.parse(atob(payload));
			return typeof decoded.exp === "number" ? decoded.exp : null;
		} catch (e) {
			return null;
		}
	},

	isExpired(): boolean {
		const exp = session.expiresAt();
		// No/unreadable expiry — let the server be the judge.
		if (exp === null) return false;
		return Date.now() / 1000 >= exp;
	},

	isAuthenticated(): boolean {
		return !!session.token() && !session.isExpired();
	},

	isAdmin(): boolean {
		const bidder = session.bidder();
		return !!bidder && (bidder.role || "").toLowerCase() === "admin";
	},

	clear(): void {
		localStorage.removeItem(TOKEN_KEY);
		localStorage.removeItem(BIDDER_KEY);
	},
};

export default session;
